/**
 * @tempest-limits props-count — FormFieldChildProps is not a component's props — it
 * is the contract handed to whatever control the field wraps, and it is exactly what
 * react-hook-form's register returns (name, value, onChange, onBlur, ref) plus what
 * the field adds around it (error, label, helperText, required, id). Trimming it
 * breaks the wiring it exists to describe.
 */
import { cloneElement, isValidElement } from "react";
import type { ReactElement, ReactNode } from "react";
import {
    Controller,
    useFormContext,
    type Control,
    type ControllerRenderProps,
    type FieldPath,
    type FieldValues,
} from "react-hook-form";

import { DUPLICATE_COPY_REMEDY } from "../utils/duplicate-instance";

export interface FormFieldChildProps {
    name: string;
    value: unknown;
    onChange: (...args: unknown[]) => void;
    onBlur: () => void;
    ref: ControllerRenderProps["ref"];
    error?: string;
    "aria-invalid"?: boolean;
    "aria-describedby"?: string;
    label?: ReactNode;
    helperText?: ReactNode;
    required?: boolean;
    id?: string;
}

export interface FormFieldProps<
    TValues extends FieldValues = FieldValues,
    TName extends FieldPath<TValues> = FieldPath<TValues>,
> {
    /** Field name (dot path supported, e.g. `"address.city"`). */
    name: TName;
    /** Field label rendered by the wrapped control. */
    label?: ReactNode;
    /** Helper text rendered by the wrapped control when there is no error. */
    helperText?: ReactNode;
    /** When `true`, marks the control as required (visual hint + native attribute). */
    required?: boolean;
    /**
     * Explicit `control` from `useForm()` / `useZodForm()`. Optional when a
     * `FormProvider` is in the tree.
     */
    control?: Control<TValues>;
    /**
     * The control to render. Receives `{ value, onChange, onBlur, ref, error, ... }`
     * via `cloneElement`. Pass `<Input />`, `<Select />`, masked inputs, etc.
     */
    children: ReactElement;
}

/**
 * Glue between `react-hook-form` `Controller` and the SDK's controlled
 * components. Wraps any control that accepts `{ value, onChange, label,
 * error }` and routes RHF state into it — eliminating the per-field
 * `<Controller render={...} />` boilerplate.
 *
 * @example
 * const form = useZodForm(schema);
 * <FormProvider {...form}>
 *     <Form>
 *         <FormField name="email" label="Email" required>
 *             <Input type="email" />
 *         </FormField>
 *         <FormField name="cep" label="CEP">
 *             <CEPInput />
 *         </FormField>
 *     </Form>
 * </FormProvider>;
 */
/**
 * What is thrown when no `control` can be resolved.
 *
 * It names **two** causes, because the throw cannot tell them apart and the
 * second one is the expensive one. The obvious reading — no provider, no prop —
 * is usually right. But the identical throw happens with a `<FormProvider>`
 * mounted directly above, when the app and the SDK resolve two different copies
 * of `react-hook-form`: the provider publishes on the app copy's context and
 * `useFormContext` here reads the SDK copy's, which is empty. Someone looking at
 * a provider they can see, being told it is missing, does not go looking at
 * `node_modules` — so the message has to send them there.
 */
const NO_CONTROL_MESSAGE =
    "FormField found no form to bind to. Either pass a `control` prop, or mount a " +
    "<FormProvider> above it. If a <FormProvider> IS mounted above it, the app and the " +
    "SDK are resolving two copies of react-hook-form: the provider publishes on one " +
    "copy's context and this field reads the other's. " +
    DUPLICATE_COPY_REMEDY;

export function FormField<
    TValues extends FieldValues = FieldValues,
    TName extends FieldPath<TValues> = FieldPath<TValues>,
>({ name, label, helperText, required, control, children }: FormFieldProps<TValues, TName>) {
    const context = useFormContext<TValues>();
    const resolvedControl = control ?? context?.control;
    if (!resolvedControl) {
        throw new Error(NO_CONTROL_MESSAGE);
    }

    return (
        <Controller
            name={name}
            control={resolvedControl}
            render={({ field, fieldState }) => {
                if (!isValidElement(children)) return children;
                const errorMessage = fieldState.error?.message;
                return cloneElement(children as ReactElement<FormFieldChildProps>, {
                    name: field.name,
                    value: field.value,
                    onChange: field.onChange,
                    onBlur: field.onBlur,
                    ref: field.ref,
                    label,
                    helperText: errorMessage ? undefined : helperText,
                    error: errorMessage,
                    required,
                    "aria-invalid": !!errorMessage,
                });
            }}
        />
    );
}
